/**
 * 확인서 발송 파이프라인 API 클라이언트 — 기회 → 판매상품(미리보기) → (확인서).
 *   GET /api/confirmations/opportunities?q=                기회 검색 + 판매상품 수
 *   GET /api/confirmations/opportunities/:id/line-items    그 기회의 판매상품(OLI)
 *   GET /api/confirmations/products?q=                     제품 선택기(Price Book)
 *   GET /api/confirmations/creators?q=                     크리에이터(Account) 검색
 * (판매상품 추가 저장 = SF 쓰기는 다음 단계. 지금은 읽기/미리보기 전용.)
 */
export class ConfirmationError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.name = 'ConfirmationError'
    this.status = status
  }
}

export interface OpportunityRow {
  oppId: string
  oppName: string
  stage: string | null
  account: string | null
  productCount: number
}

export interface LineItemRow {
  id: string
  product: string | null
  optionName: string | null
  brandItem: string | null
  department: string | null
  businessCategory: string | null
  itemCategory: string | null
  creator: string | null
  /** 크리에이터(Account) id — 확인서(Quote.Creator_Project__c) 연결 폴백. */
  creatorId: string | null
  unitPrice: number | null
  contentsFee: number | null
  pricePrj: number | null
  totalPrice: number | null
  prepayDate: string | null
  deferredDate: string | null
  uploadDate: string | null
}

export interface ProductRow {
  pbeId: string
  name: string
  code: string | null
  family: string | null
  listPrice: number | null
}

export interface CreatorRow {
  id: string
  name: string
  email: string | null
}

export interface ProductFields {
  departments: string[]
  businessCategories: string[]
  itemCategories: { value: string; businesses: string[] }[]
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(path, { credentials: 'same-origin' })
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    /* non-JSON */
  }
  if (!res.ok) {
    const code = (body as { error?: string } | null)?.error ?? `요청 실패 (${res.status})`
    throw new ConfirmationError(code, res.status)
  }
  return body as T
}

export function getConfirmationOpportunities(
  q?: string,
  limit = 50,
  offset = 0,
  ownerId?: string,
): Promise<{ rows: OpportunityRow[]; offset: number; limit: number; hasMore: boolean }> {
  const p = new URLSearchParams({ limit: String(limit), offset: String(offset) })
  if (q) p.set('q', q)
  if (ownerId) p.set('ownerId', ownerId)
  return getJson(`/api/confirmations/opportunities?${p.toString()}`)
}

/** 기회 소유자 목록(필터 드롭다운용). */
export function getOpportunityOwners(): Promise<{ rows: { id: string; name: string }[] }> {
  return getJson('/api/confirmations/opportunity-owners')
}

export function getOpportunityLineItems(oppId: string): Promise<{ oppId: string; total: number; rows: LineItemRow[] }> {
  return getJson(`/api/confirmations/opportunities/${encodeURIComponent(oppId)}/line-items`)
}

export interface QuoteRow {
  id: string
  name: string
  status: string
  quoteNumber: string
  email: string | null
  creator: string | null
  creatorId: string | null
  settlementDate: string | null
  cost: number | null
  hasPdf: boolean
  /** SF 저장 PDF 이름(SF 자동 지정, …_V1.pdf). */
  pdfName: string | null
  /** 메일 첨부 파일명 — Leferi_확인서_{크리에이터}_{브랜드} {판매상품} {월}.pdf */
  mailFileName: string
  createdBy: string | null
  createdDate: string | null
  /** 확인서 판매상품(QuoteLineItem) — 보통 크리에이터당 1건. */
  lines: QuoteLine[]
  /** 포털 메일 이력(초안/발송). 없으면 아직 초안 전. */
  mail: QuoteMail | null
}

export interface QuoteLine {
  /** 원래 기회 판매상품(OLI) id. */
  oliId: string | null
  product: string | null
  brandItem: string | null
  unitPrice: number | null
  pricePrj: number | null
  contentsFee: number | null
  totalPrice: number | null
  deferredDate: string | null
}

export interface QuoteMail {
  /** sending = 발송 중(다른 사람이 누른 직후 등). */
  status: 'drafted' | 'sending' | 'sent' | 'failed'
  to: string
  subject: string
  sentAt: string | null
  draftedAt: string
  error: string | null
}

/** 기회의 확인서(Quote, Business_Confirmation) — 저장된 PDF 첨부 여부 포함. */
export function getOpportunityQuotes(
  oppId: string,
  status?: string,
): Promise<{ oppId: string; total: number; rows: QuoteRow[] }> {
  const p = new URLSearchParams()
  if (status) p.set('status', status)
  const qs = p.toString()
  return getJson(`/api/confirmations/opportunities/${encodeURIComponent(oppId)}/quotes${qs ? `?${qs}` : ''}`)
}

export function searchProducts(q?: string, limit = 50): Promise<{ rows: ProductRow[] }> {
  const p = new URLSearchParams({ limit: String(limit) })
  if (q) p.set('q', q)
  return getJson(`/api/confirmations/products?${p.toString()}`)
}

export function searchCreators(q: string, limit = 20): Promise<{ rows: CreatorRow[] }> {
  const p = new URLSearchParams({ q, limit: String(limit) })
  return getJson(`/api/confirmations/creators?${p.toString()}`)
}

export function getProductFields(): Promise<ProductFields> {
  return getJson('/api/confirmations/product-fields')
}

/** 판매상품 draft → SF create 요청 payload (제품·크리에이터는 id). */
export interface CreateLineItemInput {
  pbeId: string
  department?: string
  brandItem?: string
  optionName?: string
  businessCategory?: string
  itemCategory?: string
  creatorId?: string | null
  unitPrice?: string
  contentsFee?: string
  pricePrj?: string
  prepayDate?: string
  deferredDate?: string
  uploadDate?: string
}

export interface CreateLineItemResult {
  index: number
  status: 'created' | 'failed'
  id?: string
  error?: string
}

export interface CreateLineItemsResponse {
  oppId: string
  summary: { total: number; created: number; failed: number }
  results: CreateLineItemResult[]
}

/** ⚠️ 프로덕션 Salesforce 에 OpportunityLineItem 을 생성한다. 확인 단계 후 호출. */
export async function createLineItems(
  oppId: string,
  items: CreateLineItemInput[],
): Promise<CreateLineItemsResponse> {
  const res = await fetch(`/api/confirmations/opportunities/${encodeURIComponent(oppId)}/line-items`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items }),
  })
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    /* non-JSON */
  }
  if (!res.ok) {
    const code = (body as { error?: string } | null)?.error ?? `요청 실패 (${res.status})`
    throw new ConfirmationError(code, res.status)
  }
  return body as CreateLineItemsResponse
}

/** 메일 발송(SMTP) 설정 상태 + 발신 표시. configured=false 면 초안 저장까지만 가능. */
export interface MailStatus {
  configured: boolean
  from: string
  fromName: string
}

export function getMailStatus(): Promise<MailStatus> {
  return getJson('/api/confirmations/mail-status')
}

/** 확인서 메일 발신 주소 — 서버에서 고정(표시용 기본값). */
export const LECOMMEND_FROM = 'cr_lecommend@leferi.com'

export interface DraftInput {
  quoteId: string
  to: string
  subject: string
  body: string
  /** 첨부 PDF 파일명(수정 가능). 비우면 기본 규칙. '.pdf' 는 없어도 서버가 붙인다. */
  fileName?: string
}

/** SF 저장 확인서 PDF 를 inline 으로 스트리밍하는 URL (iframe 미리보기용). */
export function quotePdfUrl(quoteId: string): string {
  return `/api/confirmations/quote-pdf/${encodeURIComponent(quoteId)}`
}

/** 확인서 PDF 템플릿 이름 목록(SF 템플릿과 동일 이름). */
export function getPdfTemplates(): Promise<{ rows: string[] }> {
  return getJson('/api/confirmations/pdf-templates')
}

/** 확인서 PDF 재현 생성 — 미리보기 URL(GET, 저장 안 함). */
export function generatePdfUrl(quoteId: string, template: string): string {
  const p = new URLSearchParams({ template })
  return `/api/confirmations/generate-pdf/${encodeURIComponent(quoteId)}?${p.toString()}`
}

/** 확인서 생성 전 미리보기 URL — 기회·판매상품·크리에이터 값으로 그린다(저장 안 함). */
export function previewPdfUrl(oppId: string, creatorId: string, template: string): string {
  const p = new URLSearchParams({ oppId, creatorId, template })
  return `/api/confirmations/preview-pdf?${p.toString()}`
}

/** 확인서 PDF 재현 생성 + SF(QuoteDocument) 저장. */
export async function saveGeneratedPdf(
  quoteId: string,
  template: string,
): Promise<{ ok: boolean; pdfName: string }> {
  const res = await fetch(`/api/confirmations/generate-pdf/${encodeURIComponent(quoteId)}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ template }),
  })
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    /* non-JSON */
  }
  if (!res.ok) {
    const code = (body as { error?: string } | null)?.error ?? `요청 실패 (${res.status})`
    throw new ConfirmationError(code, res.status)
  }
  return body as { ok: boolean; pdfName: string }
}

/** 확인서(Quote)에 PDF를 수동 업로드 → SF(QuoteDocument) 저장. 자동첨부와 동일 소스. */
export async function uploadQuotePdf(
  quoteId: string,
  filename: string,
  base64: string,
): Promise<{ ok: boolean; pdfName: string }> {
  const res = await fetch(`/api/confirmations/pdf/${encodeURIComponent(quoteId)}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ filename, base64 }),
  })
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    /* non-JSON */
  }
  if (!res.ok) {
    const code = (body as { error?: string } | null)?.error ?? `요청 실패 (${res.status})`
    throw new ConfirmationError(code, res.status)
  }
  return body as { ok: boolean; pdfName: string }
}
export interface DraftResult {
  quoteId: string
  status: 'created' | 'failed'
  /** 첨부될 PDF 파일명(SF 저장분). */
  attachmentName?: string
  /** PDF 가 없어 이번에 생성·SF 저장했는지. */
  generated?: boolean
  error?: string
}
export interface DraftsResponse {
  summary: { total: number; created: number; failed: number }
  results: DraftResult[]
}

async function postJson<T>(path: string, payload: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    /* non-JSON */
  }
  if (!res.ok) {
    const code = (body as { error?: string } | null)?.error ?? `요청 실패 (${res.status})`
    throw new ConfirmationError(code, res.status)
  }
  return body as T
}

/**
 * 선택 확인서별 메일 초안 저장(포털 DB, 발신 cr_lecommend 고정) — 메일은 아직 안 나감.
 * PDF 가 없는 확인서는 template 으로 생성해 SF 에 저장한다(⚠️ SF 쓰기).
 */
export function createDrafts(
  oppId: string,
  drafts: DraftInput[],
  template?: string | null,
): Promise<DraftsResponse> {
  return postJson('/api/confirmations/drafts', {
    oppId,
    drafts,
    template: template ?? undefined,
  })
}

export interface SendResponse {
  summary: { total: number; sent: number; failed: number }
  results: { quoteId: string; status: 'sent' | 'failed'; to?: string; error?: string }[]
}

/** ⚠️ 저장된 초안을 실제 발송(SMTP, 크리에이터 수신). 확인 모달 후에만 호출. */
export function sendDrafts(quoteIds: string[]): Promise<SendResponse> {
  return postJson('/api/confirmations/send', { quoteIds })
}

export interface CreateConfirmationResult {
  creatorId: string
  status: 'created' | 'exists' | 'failed'
  quoteId?: string
  lines?: number
  warning?: string
  error?: string
}

/**
 * ⚠️ 프로덕션 SF 쓰기 — 크리에이터별 비즈니스 확인서(+그 크리에이터 판매상품 행) 생성. 상태 초안.
 * 나머지 정보(이름·크리에이터/클라이언트 정보·정산일)는 SF 자동화가 채운다. 기회 단계 '제안'이면 409.
 */
export function createConfirmations(
  oppId: string,
  creatorIds: string[],
): Promise<{ summary: { total: number; created: number; failed: number }; results: CreateConfirmationResult[] }> {
  return postJson(`/api/confirmations/opportunities/${encodeURIComponent(oppId)}/confirmations`, { creatorIds })
}

export interface AssignCreatorInput {
  creatorId: string
  /** 콘텐츠제작단가 — 비우면 원본 값. */
  contentsFee?: number
  /** 운영관리비 — 비우면 원본 값. */
  pricePrj?: number
}
export interface AssignPlanStep {
  action: 'update' | 'create'
  creatorId: string
  unitPrice: number
}
export interface AssignResult {
  creatorId: string
  action: 'update' | 'create'
  status: 'ok' | 'failed'
  id?: string
  error?: string
}

/** 크리에이터 지정 미리보기 — SF 에 쓰지 않고 반영 계획만(검증 포함). */
export function previewAssignCreators(
  oppId: string,
  oliId: string,
  creators: AssignCreatorInput[],
): Promise<{ dryRun: true; plan: AssignPlanStep[] }> {
  return postJson(
    `/api/confirmations/opportunities/${encodeURIComponent(oppId)}/line-items/${encodeURIComponent(oliId)}/assign-creators`,
    { creators, dryRun: true },
  )
}

/**
 * ⚠️ 프로덕션 SF 쓰기 — 판매상품에 크리에이터 지정. 1번째 = 원본 판매상품 수정, 나머지 = 원본 복제 후 지정.
 */
export function assignCreators(
  oppId: string,
  oliId: string,
  creators: AssignCreatorInput[],
): Promise<{ summary: { total: number; ok: number; failed: number }; results: AssignResult[] }> {
  return postJson(
    `/api/confirmations/opportunities/${encodeURIComponent(oppId)}/line-items/${encodeURIComponent(oliId)}/assign-creators`,
    { creators },
  )
}

export function humanizeConfirmationError(code: string): string {
  switch (code) {
    case 'salesforce_not_configured':
      return 'Salesforce 자격증명이 서버에 설정되지 않았어요. (.env / Render env)'
    case 'salesforce_login_failed':
      return 'Salesforce 로그인 실패. 자격증명을 확인해 주세요.'
    case 'invalid_opportunity_id':
      return '기회 ID 형식이 올바르지 않아요.'
    case 'query_failed':
      return '조회에 실패했어요. 잠시 후 다시 시도해 주세요.'
    case 'smtp_not_configured':
      return '메일 발송(SMTP)이 서버에 설정되지 않았어요. (SMTP_HOST/USER/PASS)'
    case 'smtp_auth_failed':
      return '메일 서버 로그인 실패. SMTP 계정·앱 비밀번호를 확인해 주세요.'
    case 'smtp_connection_failed':
      return '메일 서버에 연결하지 못했어요. 잠시 후 다시 시도해 주세요.'
    case 'smtp_rejected_recipient':
      return '메일 서버가 받는 사람 주소를 거부했어요. 이메일 주소를 확인해 주세요.'
    case 'sending_in_progress':
      return '발송 중인 확인서예요. 잠시 후 목록을 새로고침해 주세요.'
    case 'invalid_recipient':
      return '받는 사람 이메일 형식이 올바르지 않아요.'
    case 'empty_content':
      return '제목과 본문을 입력해 주세요.'
    case 'empty_send':
      return '발송할 확인서가 없어요.'
    case 'empty_drafts':
      return '보낼 초안이 없어요.'
    case 'already_sent':
      return '이미 발송된 확인서예요.'
    case 'pdf_missing':
      return '확인서 PDF가 없어요. 템플릿을 선택하면 자동으로 생성돼요.'
    case 'no_draft':
      return '초안이 없어요. 초안을 먼저 만들어 주세요.'
    case 'stage_proposal':
      return "기회 단계가 '제안'이라 확인서를 만들 수 없어요. SF에서 판매상품 컨펌 요청 후 진행해 주세요."
    case 'no_line_items':
      return '이 크리에이터가 지정된 판매상품이 없어요.'
    case 'opportunity_not_found':
      return '기회를 찾을 수 없어요.'
    case 'already_has_creator':
      return '이미 크리에이터가 지정된 판매상품이에요. 목록을 새로고침해 주세요.'
    case 'empty_creators':
      return '크리에이터를 한 명 이상 골라 주세요.'
    case 'invalid_creators':
      return '크리에이터 선택이 올바르지 않아요(중복 등).'
    case 'line_item_not_found':
      return '판매상품을 찾을 수 없어요. 목록을 새로고침해 주세요.'
    case 'missing_recipient':
      return '받는 사람 이메일이 없어요.'
    case 'invalid_template':
      return '확인서 템플릿을 선택해 주세요.'
    case 'not_pdf':
      return 'PDF 파일이 아니에요. .pdf 파일을 올려주세요.'
    case 'empty_pdf':
      return '업로드된 PDF가 비어 있어요.'
    case 'upload_failed':
      return 'Salesforce에 PDF 저장 실패. 잠시 후 다시 시도해 주세요.'
    default:
      if (code.startsWith('smtp_send_failed')) return `메일 발송 실패 — ${code.replace(/^smtp_send_failed:?\s*/, '') || '알 수 없는 오류'}`
      return code.startsWith('요청 실패') ? code : `오류 (${code})`
  }
}
